import {
  ActionIcon,
  Button,
  Checkbox,
  Input,
  Loader,
  NumberInput,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton
} from "@marketplace/ui";
import { useParams } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { Controller, useFieldArray, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdAddCircleOutline,
  MdCheckCircle,
  MdDeleteSweep,
  MdOutlineDeleteForever,
  MdOutlineInfo
} from "react-icons/md";

import {
  deleteProductPackageModalAtom,
  deleteProductVariantModalAtom
} from "../../atoms/product-detail-page-atom";
import {
  PACKAGE_AMOUNT_MAX_LIMIT,
  PACKAGE_PRICE_MAX_LIMIT
} from "../../constants";
import type { ProductFeeTier } from "../../dtos/get-product-fee-tier-list-response-dto";
import type { AddProductFormSchema } from "../../hooks/schemas/use-add-product-form-schema";
import type { EditProductFormSchema } from "../../hooks/schemas/use-edit-product-form-schema";

type PackageSpecificationsProps = {
  packageIndex: number;
  control: any;
  register: any;
  errors: any;
};

const PackageSpecifications: React.FC<PackageSpecificationsProps> = ({
  packageIndex,
  control,
  register,
  errors
}) => {
  const { t } = useTranslation("product", {
    keyPrefix: "addProductPage.form"
  });
  const { t: tButtonTitle } = useTranslation("product", {
    keyPrefix: "buttonTitle"
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: `packages.${packageIndex}.specifications` as const
  });
  const isInitialMount = useRef(true);

  // Ensure at least one specification field exists (only on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (fields.length === 0) {
        append("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Input.Wrapper
      label={
        <>
          <span>Specification</span>
          <Tooltip
            label={
              <div className="flex flex-col gap-2">
                <div>
                  <Text type="heading" size="ss" weight="normal">
                    Hint:
                  </Text>
                </div>
                <div className="flex items-center gap-1">
                  <MdCheckCircle size={14} />

                  <Text type="heading" size="ss" weight="semibold">
                    Powerful and persuasive wording
                  </Text>
                </div>
                <div className="flex items-center gap-1">
                  <MdCheckCircle size={14} />
                  <Text type="heading" size="ss" weight="semibold">
                    Clear deliverables of the package
                  </Text>
                </div>
                <div className="flex items-center gap-1">
                  <MdCheckCircle size={14} />
                  <Text type="heading" size="ss" weight="semibold">
                    Detailed scope (quantity, limits, features)
                  </Text>
                </div>
                <div className="flex items-center gap-1">
                  <MdCheckCircle size={14} />
                  <Text type="heading" size="ss" weight="semibold">
                    Add new Unique advantages compared to other
                  </Text>
                </div>
              </div>
            }
            position="top"
            withArrow
            multiline
            classNames={{
              tooltip:
                "!rounded-lg !bg-[#454545] p-[14px] w-[276px] !text-white !text-h-ss !font-semibold"
            }}
          >
            <UnstyledButton className="flex items-center justify-center order-last ml-1">
              <MdOutlineInfo size={16} />
            </UnstyledButton>
          </Tooltip>
        </>
      }
      withAsterisk
      error={
        (errors.packages as any[])?.[packageIndex]?.specifications?.message
      }
      classNames={{
        label: "flex items-center"
      }}
    >
      <div className="flex flex-col gap-y-2">
        {fields.map((field, specIndex) => (
          <div key={field.id} className="relative pr-6">
            <TextInput
              {...register(
                `packages.${packageIndex}.specifications.${specIndex}` as const
              )}
              placeholder={t("placeholders.packageSpecification")}
              error={
                (errors.packages as any[])?.[packageIndex]?.specifications?.[
                  specIndex
                ]?.message
              }
              classNames={{
                root: "flex-1 mr-2"
              }}
            />
            <div className="absolute w-6 h-full top-0 right-0 flex items-center">
              {specIndex === 0 && (
                <ActionIcon
                  variant="subtle"
                  onClick={() => append("")}
                  className="text-noble-blue-500 hover:opacity-80"
                  title={tButtonTitle("addSpecification")}
                >
                  <MdAddCircleOutline className="h-5 w-5" />
                </ActionIcon>
              )}
              {fields.length > 1 && specIndex > 0 && (
                <ActionIcon
                  variant="subtle"
                  onClick={() => remove(specIndex)}
                  className="text-[#FF0606] hover:opacity-80"
                  title={tButtonTitle("removeSpecification")}
                >
                  <MdDeleteSweep className="h-5 w-5" />
                </ActionIcon>
              )}
            </div>
          </div>
        ))}
      </div>
    </Input.Wrapper>
  );
};

type PackageVariantsProps = {
  packageIndex: number;
  control: any;
  register: any;
  errors: any;
  isPackageFreeTrial: boolean;
  getPlatformFeePercentage: (price: number) => number;
  calculateFinalPriceWithFee: (price: number) => number;
  onDeleteVariant: (
    packageIndex: number,
    variantIndex: number,
    variantId?: number
  ) => void;
};

const PackageVariants: React.FC<PackageVariantsProps> = ({
  packageIndex,
  control,
  register,
  errors,
  isPackageFreeTrial,
  onDeleteVariant
}) => {
  const { t } = useTranslation("product", {
    keyPrefix: "addProductPage.form"
  });
  const { t: tButtonTitle } = useTranslation("product", {
    keyPrefix: "buttonTitle"
  });
  const { fields, append } = useFieldArray({
    control,
    name: `packages.${packageIndex}.variants` as const
  });
  const isInitialMount = useRef(true);

  // Ensure at least one variant exists (only on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (fields.length === 0) {
        append({
          quantity: 1,
          unit: "",
          price: 0,
          provisioningId: ""
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-y-3 mt-2">
      {fields.map((field, variantIndex) => (
        <Fragment key={field.id}>
          {variantIndex === 0 && (
            <div className="flex items-center gap-1">
              <div className="rounded-full bg-mischka-400 py-[2px] px-[10px]">
                <Text size="s" className="text-fjord-grey-500">
                  {t("labels.variants")}
                </Text>
              </div>
              <hr className="w-full h-px border-0 border-solid bg-mischka-400" />
            </div>
          )}

          <div className="flex">
            <div className="flex flex-col gap-y-2 flex-1 mr-2">
              <div className="grid grid-cols-3 gap-4">
                <Controller
                  name={
                    `packages.${packageIndex}.variants.${variantIndex}.quantity` as const
                  }
                  control={control}
                  render={({ field: quantityField }) => (
                    <NumberInput
                      value={quantityField.value}
                      onChange={quantityField.onChange}
                      label={
                        <>
                          <span>{t("labels.quantity")}</span>
                          <Tooltip
                            label={t("tooltips.variantQuantity")}
                            position="top"
                            withArrow
                            multiline
                            classNames={{
                              tooltip:
                                "!rounded-lg !bg-[#454545] p-[14px] w-[276px] !text-white !text-h-ss !font-semibold"
                            }}
                          >
                            <UnstyledButton className="flex items-center justify-center order-last ml-1">
                              <MdOutlineInfo size={16} />
                            </UnstyledButton>
                          </Tooltip>
                        </>
                      }
                      placeholder="0"
                      error={
                        (errors.packages as any[])?.[packageIndex]?.variants?.[
                          variantIndex
                        ]?.quantity?.message
                      }
                      min={1}
                      max={PACKAGE_AMOUNT_MAX_LIMIT}
                      allowDecimal={false}
                      hideControls
                      withAsterisk
                      classNames={{
                        label: "flex items-center"
                      }}
                    />
                  )}
                />
                <TextInput
                  {...register(
                    `packages.${packageIndex}.variants.${variantIndex}.unit` as const
                  )}
                  label={t("labels.unit")}
                  placeholder={t("placeholders.unit")}
                  error={
                    (errors.packages as any[])?.[packageIndex]?.variants?.[
                      variantIndex
                    ]?.unit?.message
                  }
                  withAsterisk
                />
                <Controller
                  name={
                    `packages.${packageIndex}.variants.${variantIndex}.price` as const
                  }
                  control={control}
                  render={({ field: priceField }) => (
                    <div className="flex gap-2">
                      <NumberInput
                        value={isPackageFreeTrial ? 0 : priceField.value}
                        onChange={(value) => {
                          if (!isPackageFreeTrial) {
                            priceField.onChange(value);
                          }
                        }}
                        label={t("labels.price")}
                        placeholder={t("placeholders.price")}
                        error={
                          (errors.packages as any[])?.[packageIndex]
                            ?.variants?.[variantIndex]?.price?.message
                        }
                        min={isPackageFreeTrial ? 0 : 1}
                        max={PACKAGE_PRICE_MAX_LIMIT}
                        hideControls
                        disabled={isPackageFreeTrial}
                        allowDecimal={false}
                        thousandSeparator="."
                        decimalSeparator=","
                        withAsterisk
                        classNames={{
                          root: "flex-1"
                        }}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            {variantIndex === 0 && (
              <div className="w-6 mt-8">
                <ActionIcon
                  variant="subtle"
                  onClick={() =>
                    append({
                      quantity: 1,
                      unit: "",
                      price: 0,
                      provisioningId: ""
                    })
                  }
                  className="text-noble-blue-500 hover:opacity-80"
                  title={tButtonTitle("addVariant")}
                >
                  <MdAddCircleOutline className="h-5 w-5" />
                </ActionIcon>
              </div>
            )}

            {fields.length > 1 && variantIndex > 0 && (
              <div className="w-6 mt-8">
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    const variant = fields[variantIndex] as unknown as {
                      variantId?: number;
                    };
                    onDeleteVariant(
                      packageIndex,
                      variantIndex,
                      variant?.variantId
                    );
                  }}
                  className="text-[#FF0606] hover:opacity-80"
                  title={tButtonTitle("removeVariant")}
                >
                  <MdDeleteSweep className="h-5 w-5" />
                </ActionIcon>
              </div>
            )}
          </div>

          {fields.length > 1 && variantIndex !== fields.length - 1 && (
            <hr className="w-full h-px border-0 border-solid bg-mischka-400" />
          )}
        </Fragment>
      ))}
    </div>
  );
};

type ProductPackagesProps = {
  formMethods: UseFormReturn<AddProductFormSchema | EditProductFormSchema>;
  feeTiers: ProductFeeTier[];
  isLoadingFeeTiers: boolean;
};

export const ProductFormPackages: React.FC<ProductPackagesProps> = ({
  formMethods,
  feeTiers,
  isLoadingFeeTiers
}) => {
  // Slug is needed to determine create or edit page
  const { slug } = useParams({ strict: false });
  const { t: tProduct } = useTranslation("product");
  const [, setDeleteProductPackageModal] = useAtom(
    deleteProductPackageModalAtom
  );
  const [, setDeleteProductVariantModal] = useAtom(
    deleteProductVariantModalAtom
  );

  /**
   * Get the platform fee percentage for a given price based on fee tiers
   * @param price - The base price in IDR
   * @returns The fee percentage (e.g., 3 for 3%)
   */
  const getPlatformFeePercentage = useMemo(
    () =>
      (price: number): number => {
        const tier = feeTiers.find((tier) => {
          const minPrice = tier.min_price;
          const maxPrice = tier.max_price ?? Infinity;
          return price >= minPrice && price <= maxPrice;
        });

        return tier?.fee_rate ?? 0;
      },
    [feeTiers]
  );

  /**
   * Calculate platform fee based on tiered pricing
   * @param price - The base price in IDR
   * @returns The platform fee amount
   */
  const calculatePlatformFee = useMemo(
    () =>
      (price: number): number => {
        const feePercentage = getPlatformFeePercentage(price);
        return (price / 100) * feePercentage;
      },
    [getPlatformFeePercentage]
  );

  /**
   * Calculate final price including platform fee
   * @param price - The base price in IDR
   * @returns The final price with platform fee included
   */
  const calculateFinalPriceWithFee = useMemo(
    () =>
      (price: number): number => {
        return price + calculatePlatformFee(price);
      },
    [calculatePlatformFee]
  );

  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue
  } = formMethods;

  const { fields, append, prepend, remove } = useFieldArray<
    AddProductFormSchema,
    "packages"
  >({
    control,
    name: "packages"
  });

  // Helper function to safely access package name
  const getPackageName = (index: number): string => {
    const field = fields[
      index
    ] as unknown as AddProductFormSchema["packages"][number];
    return field?.name || "";
  };

  const isFreeTrial = watch("provideTrial");

  // Handle initial free trial package setup
  useEffect(() => {
    if (isFreeTrial) {
      const hasFreeTrialPackage = fields.some(
        (pkg: AddProductFormSchema["packages"][number]) =>
          pkg.name === "Free Trial" || pkg.isFreeTrial
      );
      if (!hasFreeTrialPackage) {
        prepend({
          name: "Free Trial",
          isFreeTrial: true,
          specifications: [""],
          variants: [
            {
              quantity: 1,
              unit: "trial",
              price: 0,
              provisioningId: ""
            }
          ]
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeletePackage = (index: number) => {
    const packageToDelete = fields[
      index
    ] as unknown as AddProductFormSchema["packages"][number];

    if (slug && packageToDelete.packageId) {
      return setDeleteProductPackageModal({
        visible: true,
        data: {
          packageId: packageToDelete.packageId,
          packageName: packageToDelete.name
        }
      });
    }

    if (slug && packageToDelete.isFreeTrial && packageToDelete.packageId) {
      return setDeleteProductPackageModal({
        visible: true,
        data: {
          packageId: packageToDelete.packageId,
          packageName: packageToDelete.name
        }
      });
    }

    return remove(index);
  };

  const isPackageFreeTrial = (index: number) => {
    const packageName = getPackageName(index);
    const field = fields[
      index
    ] as unknown as AddProductFormSchema["packages"][number];
    return packageName === "Free Trial" || field?.isFreeTrial;
  };

  const handleDeleteVariant = (
    packageIndex: number,
    variantIndex: number,
    variantId?: number
  ) => {
    // If we're in edit page and variant has an ID, show confirmation modal
    if (slug && variantId) {
      return setDeleteProductVariantModal({
        visible: true,
        data: {
          variantId,
          packageIndex,
          variantIndex
        }
      });
    }

    // Otherwise, just remove from the form array directly
    const currentPackages = formMethods.getValues("packages");
    const updatedVariants = currentPackages[packageIndex].variants.filter(
      (_: unknown, idx: number) => idx !== variantIndex
    );
    formMethods.setValue(`packages.${packageIndex}.variants`, updatedVariants);
  };

  useEffect(() => {
    const checkIsThereFreeTrialPackage = () => {
      return fields.some(
        (pkg: AddProductFormSchema["packages"][number]) =>
          pkg.name === "Free Trial" || pkg.isFreeTrial
      );
    };

    if (checkIsThereFreeTrialPackage()) {
      return setValue("provideTrial", true);
    }
    return setValue("provideTrial", false);
  }, [isFreeTrial, fields, setValue]);

  // Show loader while fetching fee tiers
  if (isLoadingFeeTiers) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader size="md" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-4">
      {fields.map((_field, index) => (
        <div className="flex flex-col gap-y-2" key={_field.id}>
          <div className="flex items-center justify-between gap-2">
            <Text size="xl" className="text-fjord-grey-500">
              Package {index + 1}
            </Text>
            <hr className="flex-1 h-px border-0 border-solid bg-mischka-400" />
            {fields.length > 1 && (
              <UnstyledButton
                onClick={() => handleDeletePackage(index)}
                className="flex items-center gap-x-1 text-[#FF0606] hover:opacity-80"
                title={tProduct("buttonTitle.removePackage")}
              >
                <Text size="xl">delete</Text>
                <MdOutlineDeleteForever className="h-6 w-6" />
              </UnstyledButton>
            )}
          </div>

          <TextInput
            {...register(`packages.${index}.name` as const)}
            label={tProduct("addProductPage.form.labels.packageName")}
            placeholder={tProduct(
              "addProductPage.form.placeholders.packageName"
            )}
            error={(errors.packages as any[])?.[index]?.name?.message}
            withAsterisk
          />

          {/* Package Specifications */}
          <PackageSpecifications
            packageIndex={index}
            control={control}
            register={register}
            errors={errors}
          />

          {/* Package Variants */}
          <PackageVariants
            packageIndex={index}
            control={control}
            register={register}
            errors={errors}
            isPackageFreeTrial={isPackageFreeTrial(index)}
            getPlatformFeePercentage={getPlatformFeePercentage}
            calculateFinalPriceWithFee={calculateFinalPriceWithFee}
            onDeleteVariant={handleDeleteVariant}
          />

          {/* Separator between packages */}
          {index < fields.length - 1 && (
            <hr className="w-full h-px border-0 border-solid bg-mischka-300 mt-2" />
          )}
        </div>
      ))}

      <Button
        fullWidth
        variant="secondary"
        size="sm"
        classNames={{
          label: "text-fjord-grey-500",
          section: "text-fjord-grey-500"
        }}
        onClick={() =>
          append({
            name: `Package ${fields.length + 1}`,
            isFreeTrial: false,
            specifications: [""],
            variants: [
              {
                quantity: 1,
                unit: "",
                price: 0,
                provisioningId: ""
              }
            ]
          })
        }
        title={tProduct("buttonTitle.addPackage")}
        rightSection={<MdAdd size={14} />}
      >
        {tProduct("addProductPage.form.buttons.addPackage")}
      </Button>

      <div>
        <Controller
          name="provideTrial"
          control={control}
          render={({ field }) => (
            <Checkbox
              label={tProduct("addProductPage.form.labels.provideFreeTrial")}
              classNames={{
                label: "text-h-xxs !font-bold"
              }}
              checked={field.value}
              onChange={(e) => {
                const isChecked = e.target.checked;
                field.onChange(isChecked);

                // If enabling free trial, add the free trial package
                if (isChecked) {
                  // First, check if we already have a free trial package
                  const hasFreeTrial = fields.some(
                    (pkg: AddProductFormSchema["packages"][number]) =>
                      pkg.name === "Free Trial" || pkg.isFreeTrial
                  );
                  if (!hasFreeTrial) {
                    prepend({
                      name: "Free Trial",
                      isFreeTrial: true,
                      specifications: [""],
                      variants: [
                        {
                          quantity: 1,
                          unit: "trial",
                          price: 0,
                          provisioningId: ""
                        }
                      ]
                    });
                  }
                } else {
                  // If disabling free trial, remove the free trial package
                  const freeTrialIndex = fields.findIndex(
                    (pkg: AddProductFormSchema["packages"][number]) =>
                      pkg.name === "Free Trial" || pkg.isFreeTrial
                  );
                  if (freeTrialIndex !== -1) {
                    handleDeletePackage(freeTrialIndex);
                  }
                }
              }}
              error={(errors.provideTrial as any)?.message}
            />
          )}
        />
      </div>
    </div>
  );
};
